import { useEffect, useRef, useState } from "react";
import type { BatchState } from "@/lib/tool-kit/identifiers/batch";
import { lookupEach } from "@/lib/tool-kit/identifiers/lookup";

interface RunRow {
  id: number;
  state: BatchState;
}

export interface LookupProgress {
  done: number;
  total: number;
}

/**
 * The state of one batch of lookups, shared by the identifier converters.
 *
 * `run` takes the planned rows, looks up the `pending` ones one at a time, and merges
 * each result back with `apply`. Starting a new run — or leaving the page — aborts the
 * one in flight, and a superseded run never overwrites the newer one's rows.
 */
export function useLookupRun<Row extends RunRow>() {
  const [rows, setRows] = useState<Row[]>([]);
  const [progress, setProgress] = useState<LookupProgress | null>(null);
  const [failed, setFailed] = useState(false);
  const current = useRef<AbortController | null>(null);

  useEffect(() => () => current.current?.abort(), []);

  async function run<Result>(
    planned: Row[],
    lookup: (row: Row, signal: AbortSignal) => Promise<Result>,
    apply: (row: Row, result: Result) => Row
  ): Promise<void> {
    current.current?.abort();
    const controller = new AbortController();
    current.current = controller;
    const queue = planned.filter((row) => row.state === "pending");
    setRows(planned);
    setFailed(false);
    setProgress({ done: 0, total: queue.length });
    try {
      const results = await lookupEach(
        queue,
        (row) => lookup(row, controller.signal),
        (done) => setProgress({ done, total: queue.length }),
        controller.signal
      );
      if (!results) {
        return;
      }
      const byId = new Map(queue.map((row, index) => [row.id, results[index]]));
      setRows(
        planned.map((row) =>
          byId.has(row.id) ? apply(row, byId.get(row.id) as Result) : row
        )
      );
    } catch {
      setFailed(true);
    } finally {
      if (current.current === controller) {
        setProgress(null);
      }
    }
  }

  return { rows, setRows, progress, failed, run };
}
